import React from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowRight, ShoppingBag, Star, Users, TrendingUp, Tag } from 'lucide-react'
import { productService, categoryService, sliderService, settingsService } from '../services/api'
import ProductCard from '../components/ProductCard'
import LoadingSpinner from '../components/LoadingSpinner'
import ModernSlider from '../components/ModernSlider'
import Logo from '../components/Logo'
import MetaTags from '../components/MetaTags'
import '../styles/CategoryCard.css'

const Home = () => {
  const { data: featuredProducts, isLoading } = useQuery({
    queryKey: ['featuredProducts'],
    queryFn: () => productService.getFeaturedProducts(8),
  })

  const { data: settings } = useQuery({
    queryKey: ['storeSettings'],
    queryFn: settingsService.getSettings,
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  const { data: categoriesData, isLoading: categoriesLoading } = useQuery({
    queryKey: ['homeCategories'],
    queryFn: () => categoryService.getCategories(true),
    retry: false,
  })

  const { data: allProductsData, isLoading: allProductsLoading } = useQuery({
    queryKey: ['allProducts'],
    queryFn: () => productService.getProducts({ limit: 12, page: 1 }),
    retry: false,
  })

  // Get slider data
  const { data: slides } = useQuery({
    queryKey: ['slider-images'],
    queryFn: () => sliderService.getSlides(),
    refetchOnWindowFocus: false,
  })

  const categories = categoriesData?.flat_categories?.filter(cat => cat.product_count > 0)?.slice(0, 6) || []
  const allProducts = allProductsData?.products || []
  const hasSliderImages = slides && slides.length > 0

  return (
    <div className="min-h-screen bg-white">
      <MetaTags 
        title={settings?.store_name ? `${settings.store_name} - نخبة المسابيح` : 'سبحات - نخبة المسابيح'}
        description={settings?.store_description || 'نقدم لكم أجود أنواع المسابيح والسبح بأفضل الأسعار. جودة عالية وخدمة متميزة.'}
        keywords="مسابيح، سبح، تسبيح، إسلامي، كويت، سبحات"
      />
      {/* Hero Section */}
      <section
        className="text-white"
        style={{ backgroundColor: 'var(--color-primary) !important' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          {/* Mobile: Centered Layout */}
          <div className="block lg:hidden text-center min-h-screen flex flex-col items-center justify-center">
            {/* Logo */}
            <div className="mb-8">
              <Logo variant="hero" showText={false} />
            </div>
            
            {/* Hero Text */}
            <div className="space-y-6">
              <h1 className="text-4xl md:text-6xl font-bold text-white">
                {settings?.store_name || 'سبحات'}
              </h1>
              <p className="text-xl md:text-2xl text-white">
                {settings?.store_description || 'نخبة المسابيح'}
              </p>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mt-10">
                <Link
                  to="/products"
                  className="btn btn-lg text-white hover:opacity-90 inline-flex items-center space-x-2 bg-[var(--btn-primary-bg)] hover:bg-[var(--btn-primary-hover)]"
                >
                  <ShoppingBag className="h-5 w-5" />
                  <span>تسوق الآن</span>
                </Link>
              </div>
            </div>
          </div>

          {/* Desktop: Original 2-Column Layout */}
          <div className="hidden lg:grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-6xl font-bold mb-6 text-balance text-white">
                {settings?.store_name || 'سبحات'}
              </h1>
              <p className="text-xl md:text-2xl mb-8 text-white">
                {settings?.store_description || 'نخبة المسابيح'}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/products"
                  className="btn btn-lg text-white hover:opacity-90 inline-flex items-center space-x-2 bg-[var(--btn-primary-bg)] hover:bg-[var(--btn-primary-hover)]"
                >
                  <ShoppingBag className="h-5 w-5" />
                  <span>تسوق الآن</span>
                </Link>
              </div>
            </div>
            <Logo variant="hero" showText={false} />
          </div>
        </div>
      </section>

      {/* Slider Section - positioned after hero */}
      {hasSliderImages && (
        <section className="py-8 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ModernSlider />
          </div>
        </section>
      )}

      {/* Categories Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              تسوق حسب الفئة
            </h2>
            <p className="text-xl text-gray-600">
              استكشف مجموعة واسعة من فئات المنتجات
            </p>
          </div>

          {categoriesLoading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="xl" />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
                {categories.map((category) => (
                  <Link
                    key={category.id}
                    to={`/category/${category.slug}`}
                    className="category-card group"
                  >
                    <div className="category-card-inner">
                      {/* Front Side - Image */}
                      <div className="category-card-front">
                        {category.image ? (
                          <img
                            src={`/uploads/categories/${category.image}`}
                            alt={category.name}
                            className="category-front-image"
                          />
                        ) : (
                          <div className="category-front-fallback">
                            <Tag className="category-front-icon" />
                          </div>
                        )}
                      </div>
                      
                      {/* Back Side - Name and Count */}
                      <div className="category-card-back">
                        <div className="category-back-content">
                          <div className="category-back-icon-wrapper">
                            <Tag className="category-back-icon" />
                          </div>
                          <h3 className="category-back-name">
                            {category.name}
                          </h3>
                          <p className="category-back-count">
                            {category.product_count} منتج
                          </p>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
              
              {categories.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-subtle">لا توجد فئات متاحة بمنتجات</p>
                </div>
              )}
              
              <div className="text-center mt-12">
                <Link
                  to="/categories"
                  className="btn btn-lg inline-flex items-center space-x-2 bg-[var(--btn-primary-bg)] text-[var(--btn-primary-text)] hover:bg-[var(--btn-primary-hover)]"
                >
                  <span>عرض جميع الفئات</span>
                  <ArrowRight className="h-5 w-5" />
                </Link>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              المنتجات المميزة
            </h2>
            <p className="text-xl text-gray-600">
              اكتشف مجموعتنا المختارة بعناية من المنتجات المميزة
            </p>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="xl" />
            </div>
          ) : (
            <>
              <div className="cards">
                {featuredProducts?.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
              
              {featuredProducts?.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-subtle">لا توجد منتجات مميزة متاحة</p>
                </div>
              )}
              
              <div className="text-center mt-12">
                <Link
                  to="/products"
                  className="btn btn-lg inline-flex items-center space-x-2 bg-[var(--btn-primary-bg)] text-[var(--btn-primary-text)] hover:bg-[var(--btn-primary-hover)]"
                >
                  <span>عرض جميع المنتجات</span>
                  <ArrowRight className="h-5 w-5" />
                </Link>
              </div>
            </>
          )}
        </div>
      </section>

      {/* All Products Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              أحدث المنتجات
            </h2>
            <p className="text-xl text-gray-600">
              استكشف أحدث الوافدين والعناصر الرائجة
            </p>
          </div>

          {allProductsLoading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="xl" />
            </div>
          ) : (
            <>
              <div className="cards">
                {allProducts?.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
              
              {allProducts?.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-subtle">لا توجد منتجات متاحة</p>
                </div>
              )}
              
              <div className="text-center mt-12">
                <Link
                  to="/products"
                  className="btn btn-lg inline-flex items-center space-x-2 bg-[var(--btn-primary-bg)] text-[var(--btn-primary-text)] hover:bg-[var(--btn-primary-hover)]"
                >
                  <span>عرض جميع المنتجات</span>
                  <ArrowRight className="h-5 w-5" />
                </Link>
              </div>
            </>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="py-16 text-white"
        style={{ background: 'var(--color-secondary)' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
            مستعد لبدء التسوق؟
          </h2>
          <p className="text-xl mb-8 text-white">
            انضم إلى آلاف العملاء الراضين الذين يثقون في منصتنا
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/products"
              className="btn btn-lg text-white hover:opacity-90 bg-[var(--btn-primary-bg)] hover:bg-[var(--btn-primary-hover)]"
            >
              تصفح المنتجات
            </Link>
            <Link
              to="/register"
              className="btn btn-lg bg-white hover:bg-gray-100 border border-white text-[var(--color-secondary)]"
            >
              إنشاء حساب
            </Link>
          </div>
        </div>
      </section>

     
    </div>
  )
}

export default Home
